import { Search } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";
import { searchAddressByZipCode } from "../services/CepService";
import { CommercialAddress } from "../types/proposal";
import { formatZipCode } from "../utils/masks";

type Props = {
  address: CommercialAddress;
  onChange: (address: CommercialAddress) => void;
};

const CommercialAddressFields: React.FC<Props> = ({ address, onChange }) => {
  const [searchingZipCode, setSearchingZipCode] = useState(false);
  const [zipCodeError, setZipCodeError] = useState("");

  const update = (field: keyof CommercialAddress, value: string) => {
    onChange({
      ...address,
      [field]: field === "zipCode" ? formatZipCode(value) : value,
    });
  };

  const handleSearchZipCode = async () => {
    setSearchingZipCode(true);
    setZipCodeError("");
    try {
      const foundAddress = await searchAddressByZipCode(address.zipCode);
      onChange({
        ...address,
        ...foundAddress,
        number: address.number,
        complement: address.complement || foundAddress.complement || "",
      });
    } catch (error) {
      setZipCodeError(
        error instanceof Error
          ? error.message
          : "Não foi possível consultar o CEP.",
      );
    } finally {
      setSearchingZipCode(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4} md={3}>
        <TextField
          fullWidth
          label="CEP"
          value={address.zipCode}
          onChange={event => update("zipCode", event.target.value)}
          onKeyDown={event => {
            if (event.key === "Enter") {
              event.preventDefault();
              void handleSearchZipCode();
            }
          }}
          inputProps={{ inputMode: "numeric", maxLength: 9 }}
          error={Boolean(zipCodeError)}
          helperText={zipCodeError || "Digite o CEP e clique na lupa"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Buscar endereço pelo CEP">
                  <span>
                    <IconButton
                      edge="end"
                      aria-label="Buscar endereço pelo CEP"
                      disabled={searchingZipCode}
                      onClick={() => void handleSearchZipCode()}
                    >
                      {searchingZipCode ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Search />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={8} md={6}>
        <TextField
          fullWidth
          label="Endereço"
          value={address.street}
          onChange={event => update("street", event.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={4} md={3}>
        <TextField
          fullWidth
          label="Número"
          value={address.number}
          onChange={event => update("number", event.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={8} md={5}>
        <TextField
          fullWidth
          label="Complemento"
          value={address.complement}
          onChange={event => update("complement", event.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          label="Bairro"
          value={address.neighborhood}
          onChange={event => update("neighborhood", event.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={4} md={3}>
        <TextField
          fullWidth
          label="Cidade"
          value={address.city}
          onChange={event => update("city", event.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={2} md={1}>
        <TextField
          fullWidth
          label="UF"
          value={address.state}
          onChange={event =>
            update("state", event.target.value.toUpperCase().slice(0, 2))
          }
          inputProps={{ maxLength: 2 }}
        />
      </Grid>
    </Grid>
  );
};

export default CommercialAddressFields;
